import { Component, ErrorInfo, ReactNode } from 'react';
import { RouteErrorFallback } from './RouteErrorFallback';

interface RouteErrorBoundaryProps {
  /** Changes on navigation (RootLayout passes location.pathname) → auto-reset. */
  resetKey: string;
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
}

/**
 * Route-level error boundary (BUGS #9 / Phase 1). Wraps ONLY the routed
 * content inside RootLayout, so a page that throws during render shows a
 * plain-language panel while the shell, nav, and sync indicators stay alive —
 * field users never get a white screen. React requires a class component for
 * componentDidCatch; errors go to console.error (no external reporting
 * service exists — deliberately not added).
 */
export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RouteErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Route render error:', error, info.componentStack);
  }

  componentDidUpdate(prev: RouteErrorBoundaryProps) {
    // Navigating away recovers: a new route gets a fresh try.
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  reset = () => this.setState({ hasError: false });

  render() {
    if (this.state.hasError) return <RouteErrorFallback onReset={this.reset} />;
    return this.props.children;
  }
}
